import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link } from 'react-router';
import cn from 'classnames';

import ProductUtils from 'helpers/ProductUtils';
import useMartyContext from 'hooks/useMartyContext';
import { CART_UPSELL_RECO_KEY, fetchCartUpsellRecos, showCartModal } from 'actions/cart';
import { receiveRecos } from 'actions/recos';
import { onCartRecoClick } from 'store/ducks/cart/actions';
import CardMedia from 'components/common/card/CardMedia';
import MelodyCarousel from 'components/common/MelodyCarousel';
import getCardData from 'data/getCardData';
import { track } from 'apis/amethyst';
import { evRecommendationImpressionWrapper } from 'events/recommendations';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { AppState } from 'types/app';
import { CartItem } from 'types/mafia';

import css from 'styles/components/cart/cartUpsellRecos.scss';

interface OwnProps {
  isFullWidth?: boolean;
  item?: CartItem;
}

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = OwnProps & PropsFromRedux;

export const CartUpsellRecos = (props: Props) => {
  const { testId } = useMartyContext();

  const {
    cart: { cartObj: { activeItems = [] } = {} },
    recos: { recos, title } = {},
    fetchCartUpsellRecos,
    receiveRecos,
    onCartRecoClick,
    showCartModal,
    item,
    isFullWidth
  } = props;

  useEffect(() => {
    if (Array.isArray(recos) && recos.length) {
      const recommendationImpression = [{
        numberOfRecommendations: recos.length,
        recommendationType: 'PRODUCT_RECOMMENDATION',
        recommendationSource: 'EP13N',
        widgetType: 'COMPLETE_THE_LOOK'
      }];
      track(() => ([
        evRecommendationImpressionWrapper, { recommendationImpression }
      ]));
    }
  }, [recos]);

  useEffect(() => {
    // fetch recently added item recos, or single item in cart recos
    if (item) {
      fetchCartUpsellRecos({ activeItems: [item] });
    } else {
      fetchCartUpsellRecos({ activeItems });
    }

    // clean up cart recos afterward
    return () => {
      receiveRecos(CART_UPSELL_RECO_KEY, {}, '');
    };
  }, [activeItems, fetchCartUpsellRecos, item, receiveRecos]);

  const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const { productId, index } = e.currentTarget.dataset;
    showCartModal(false);
    onCartRecoClick(productId, index, 'COMPLETE_THE_LOOK');
  };

  if (!Array.isArray(recos)) {
    return null;
  }

  if (!recos?.length) {
    return null;
  }

  return (
    <div className={cn(css.container, { [css.fullWidth]: isFullWidth })} data-test-id={testId('recoCarousel')}>
      <h3>{title}</h3>
      <MelodyCarousel>
        {recos.map((item, index) => {
          const { styleId, productUrl, brandName, productName, productId, price, originalPrice } = item;
          const isDiscounted = ProductUtils.isStyleOnSale({ originalPrice, price });
          const { media } = getCardData({ ...item, index });
          return (
            <Link
              to={productUrl}
              onClick={handleOnClick}
              className={css.item}
              key={styleId}
              data-product-id={productId}
              data-index={index}
              data-test-id={testId('cartReco')}>
              <CardMedia {...media} />
              <span data-test-id={testId('recoBrandName')}>{brandName}</span>
              <span data-test-id={testId('recoProductName')}>{productName}</span>
              <div className={cn(css.priceContainer)}>
                <span data-test-id={testId('recoPrice')} className={cn(css.price, { [css.discount]: isDiscounted })}>{price}</span>
                {isDiscounted && <span className={css.msrp} data-test-id={testId('msrpPrice')}>{originalPrice}</span>}
              </div>
            </Link>
          );
        }
        )}
      </MelodyCarousel>
    </div>
  );
};

export const mapStateToProps = (state: AppState) => {
  const {
    cart,
    recos
  } = state;

  return {
    cart,
    recos: recos.janus?.['cart_upsell']
  };
};

const connector = connect(mapStateToProps, { fetchCartUpsellRecos, onCartRecoClick, showCartModal, receiveRecos });
const ConnectedCartUpsellRecos = connector(CartUpsellRecos);
export default withErrorBoundary('ConnectedCartUpsellRecos', ConnectedCartUpsellRecos);
